import produce from 'immer';
import create, { StoreApi } from 'zustand';

export const usePortalLoadingStore = create<IGlobalLoadingStore>((set, get) => ({
  ...getLoadingState(),
  actions: {
    ...createLoadingActions(get, set),
  },
}));

/**
 *
 * @returns slice of PortalStore state related to favorites sidebar panel.
 */
export function getLoadingState() {
  return {
    loading: {
      /** show global loader */
      global: new Map<string, boolean>(),
      /** individual loader Ids */
      others: new Map<string, boolean>(),
    },
  };
}

type IGlobalLoadingStore = ReturnType<typeof getLoadingState> & { actions: ReturnType<typeof createLoadingActions> };

/**
 * @param get zustand store getter
 * @param set zustand store setter
 * @returns slice of PortalStore actions related to favorites panel
 */
export function createLoadingActions(
  get: StoreApi<IGlobalLoadingStore>['getState'],
  set: StoreApi<IGlobalLoadingStore>['setState']
) {
  return {
    /**
     * Show global spinner
     * @param id of element showing global spinner, for tracking.
     * @returns early if it's already active.
     */
    startLoadingGlobally(id: string) {
      if (get().loading.global.get(id)) {
        return;
      }

      set(
        produce<IGlobalLoadingStore>(state => {
          state.loading.global.set(id, true);
        })
      );
    },
    /**
     * Tell store an element is done showing global spinner.
     * @param id of element previously showing global spinner
     */
    doneLoadingGlobally(id: string) {
      set(
        produce<IGlobalLoadingStore>(state => {
          state.loading.global.delete(id);
        })
      );
    },
    /**
     * If the busy-tracking ID of something needs to update, e.g. you just learned the pageName of a folder after
     * fetching FolderViewData.
     *
     * Attempting to update an ID that is not there, will be a NOOP.
     *
     * @param newId id to update
     * @param oldId previous ID
     */
    updateBusyId(newId: string, oldId: string) {
      const currentBusyState = getGlobalLoadingState(get()).get(oldId);
      if (currentBusyState === undefined) {
        return; // we should not update something that isn't there, or we'll mess with busy by size calculation.
      }
      set(
        produce<IGlobalLoadingStore>(state => {
          state.loading.global.set(newId, !!state.loading.global.get(oldId));
          state.loading.global.delete(oldId);
        })
      );
    },
    addActiveLoader(id: string) {
      set(
        produce<IGlobalLoadingStore>(state => {
          state.loading.others.set(id, true);
        })
      );
    },
    removeActiveLoader(id: string) {
      set(
        produce<IGlobalLoadingStore>(state => {
          state.loading.others.delete(id);
        })
      );
    },
  };
}

//#region selectors

export function getGlobalLoadingState(state: IGlobalLoadingStore) {
  return state.loading.global;
}

export function getSpecificLoadingState(state: IGlobalLoadingStore, id: string) {
  return !!getGlobalLoadingState(state).get(id);
}

export function getIsGlobalLoading(state: IGlobalLoadingStore) {
  // this could also work on the number of individual active loaders
  return getGlobalLoadingState(state).size > 0;
}

//#endregion
